import { useEffect, useRef, useState } from 'react';
import { normalizeApiError } from '../utils/normalizeError';

export const AVATAR_UPLOAD_DEADLINE_MS = 60000;
export const AVATAR_REMOVAL_DEADLINE_MS = 30000;
export const AVATAR_REFRESH_DEADLINE_MS = 15000;

const pendingPhases = new Set(['uploading', 'removing', 'refreshing']);

// Local to profile avatars: never invalidate or cancel a shared auth refresh.
export function useAvatarOperation(ownerKey, apiClient) {
  const [state, setState] = useState({ phase: 'idle', message: '' });
  const active = useRef(null);
  const mounted = useRef(false);
  const owner = useRef(ownerKey);
  owner.current = ownerKey;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      active.current?.controller.abort();
      active.current = null;
    };
  }, []);

  useEffect(() => {
    const previous = active.current;
    if (previous && previous.ownerKey !== ownerKey) {
      previous.controller.abort();
      active.current = null;
      setState({ phase: 'idle', message: '' });
    }
  }, [ownerKey]);

  const cancel = () => active.current?.controller.abort();

  const run = async (kind, { request, onMutationSuccess, onRefreshSuccess }) => {
    // Ref, rather than rendered disabled state, closes same-tick double clicks.
    if (active.current || !mounted.current) return;
    const operation = { ownerKey, controller: new AbortController(), phase: kind === 'upload' ? 'uploading' : 'removing' };
    active.current = operation;
    const current = () => mounted.current && active.current === operation && owner.current === operation.ownerKey;
    const stopped = () => Object.assign(new Error('Avatar operation stopped'), {
      avatarKind: operation.timedOut ? 'timeout' : 'cancelled',
    });

    // The outer deadline also bounds waits inside Axios request interceptors.
    // Axios timeout alone starts too late to bound that part of the lifecycle.
    const bounded = (send, milliseconds) => new Promise((resolve, reject) => {
      let settled = false;
      let timer;
      const expiresAt = Date.now() + milliseconds;
      const finish = (callback, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        operation.controller.signal.removeEventListener('abort', aborted);
        callback(value);
      };
      const aborted = () => finish(reject, stopped());
      const expire = () => {
        operation.timedOut = true;
        operation.controller.abort();
      };
      timer = setTimeout(expire, milliseconds);
      operation.controller.signal.addEventListener('abort', aborted, { once: true });
      if (operation.controller.signal.aborted) aborted();
      Promise.resolve().then(() => {
        if (!current() || operation.controller.signal.aborted) throw stopped();
        return send({ timeout: milliseconds, signal: operation.controller.signal });
      }).then(value => {
        // Mobile backgrounding can defer timers. Do not accept an expired
        // response simply because it resumes before the timer callback.
        if (Date.now() >= expiresAt) expire(); else finish(resolve, value);
      }, error => {
        if (Date.now() >= expiresAt) expire(); else finish(reject, error);
      });
    });

    try {
      setState({ phase: operation.phase, kind, message: '' });
      const response = await bounded(request, kind === 'upload' ? AVATAR_UPLOAD_DEADLINE_MS : AVATAR_REMOVAL_DEADLINE_MS);
      if (!current() || operation.controller.signal.aborted) return;
      onMutationSuccess(response.data);
      operation.phase = 'refreshing';
      setState({ phase: 'refreshing', kind, message: '' });
      const refreshed = await bounded(config => apiClient.get('/auth/me', config), AVATAR_REFRESH_DEADLINE_MS);
      if (!current() || operation.controller.signal.aborted) return;
      onRefreshSuccess(refreshed.data);
      setState({ phase: 'completed', kind, message: kind === 'upload' ? 'Photo uploaded.' : 'Photo removed.' });
    } catch (error) {
      if (!current()) return;
      const status = Number.isInteger(error?.response?.status) ? error.response.status : undefined;
      const failure = ['timeout', 'cancelled'].includes(error?.avatarKind) ? error.avatarKind
        : ['ECONNABORTED', 'ETIMEDOUT'].includes(error?.code) ? 'timeout'
          : error?.code === 'ERR_CANCELED' ? 'cancelled' : status ? 'http_error' : 'network_error';
      const refresh = operation.phase === 'refreshing';
      const category = refresh ? `${kind}_succeeded_refresh_${failure}` : `${kind}_${failure}`;
      // Never log the error/config/response object: it can contain credentials,
      // file names, private URLs or request bodies. Only fixed categories/status.
      console.warn('[AVATAR_OPERATION]', { category, ...(status === undefined ? {} : { status }) });
      const action = kind === 'upload' ? 'upload' : 'removal';
      let message;
      if (refresh) {
        message = `Photo ${kind === 'upload' ? 'uploaded' : 'removed'}, but profile refresh ${failure === 'timeout' ? 'timed out' : 'failed'}. Refresh the page to check the result.`;
      } else if (failure === 'timeout' || failure === 'cancelled') {
        message = `Photo ${action} ${failure === 'timeout' ? 'timed out' : 'cancelled'}. It may still complete on the server. Refresh your profile before retrying.`;
      } else {
        message = failure === 'http_error' ? normalizeApiError(error) : `Photo ${action} failed. Check your connection and try again.`;
      }
      setState({ phase: 'failed', kind, message });
    } finally {
      // Aborting does not prove a server-side write was cancelled.
      operation.controller.abort();
      if (active.current === operation) active.current = null;
    }
  };

  return { ...state, pending: pendingPhases.has(state.phase), run, cancel };
}
